/**
 * MED-938 / E3.5 — unified "Upcoming Sessions" list across all modalities,
 * sorted chronologically. Renders the colored date block, session meta, status
 * badge (LIVE NOW / Tomorrow / In X weeks), and — for Peer Led cohorts — a
 * persistent "Leading" badge + "Start Session Prep" CTA on led sessions
 * (states 1/5). Live sessions get a "Join session" CTA.
 *
 * MED-1141 — pre-session content stays reachable until session start. Within
 * the unlock→start window the card surfaces a link to the pre-session page so
 * participants/leaders can return to it after finishing prep (the UAT gap):
 *   - Non-leading participant on a group session whose reflection is unlocked
 *     (reflection_available) gets a "Complete" action that becomes "Review"
 *     once their reflection is complete — a net-new action (participants had
 *     none in Upcoming before). Routes to the reflection page.
 *   - Leader prep action reads "Start Session Prep" while prep is outstanding
 *     and "Review prep" once done — it no longer disappears after completion.
 * Both give way to the live/join treatment the moment the session starts
 * (is_live), so no Complete/Review/prep action lingers past session start. The
 * label depends only on completion state, never a separate "started" state.
 */
import { Badge, Box, Button, Card, Divider, Group, Stack, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { Calendar } from 'react-feather';
import type { DashboardRefreshUpcomingSession } from '../../../services/api/participantPortal.js';
import { DateBlock } from './DateBlock.js';
import {
  formatDayTime,
  formatDuration,
  sessionLabel,
  upcomingBadge,
  upcomingDateTone,
} from './dashboardRefreshUtils.js';

function metaLine(session: DashboardRefreshUpcomingSession): string {
  const parts = [formatDayTime(session.scheduled_start)];
  const duration = formatDuration(session.duration_minutes);
  if (duration) parts.push(duration);
  if (session.group_name) parts.push(session.group_name);
  return parts.join(' · ');
}

function SessionRow({
  session,
  cohortId,
}: {
  session: DashboardRefreshUpcomingSession;
  cohortId: string | null;
}) {
  const badge = upcomingBadge(session);
  const reflectionPath = `/participant/sessions/${session.id}/reflection`;
  const waitingRoomPath = `/participant/sessions/${session.id}/waiting-room`;
  // MED-1127 (AC11): a live leader must rejoin their own active session — the
  // shared CoachActiveSessionPage at the leader active route — NOT the
  // participant waiting room (same class of leak MED-1045 fixed). The route
  // needs a cohortId; if it's somehow absent, fall back to prep (leader-safe,
  // never the waiting room).
  const leaderActivePath = cohortId
    ? `/participant/cohorts/${cohortId}/sessions/${session.id}/active`
    : reflectionPath;
  // MED-1062 (E4.0h): a no-show 'missed' session is pending reschedule — it
  // never happened, so suppress every session-entry affordance (the "Leading"
  // badge, Join, and Start Session Prep) for the leader AND participants. Only
  // the amber "PENDING RESCHEDULE" pill remains.
  const isMissed = session.status === 'missed';
  // MED-1141: a non-leading participant on a group session whose reflection has
  // unlocked keeps a link to the reflection page for the whole unlock→start
  // window. Gating on reflection_available defers the "not unlocked yet" rule to
  // the availability story; leaders use the prep action below instead (and are
  // redirected reflection→prep on the page itself), so this is participant-only.
  const canReachReflection =
    !session.is_leading &&
    session.modality_type === 'group_coaching' &&
    session.reflection_available;

  return (
    <Group justify="space-between" wrap="nowrap" gap="md" py="xs">
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        <DateBlock iso={session.scheduled_start} tone={upcomingDateTone(session)} />
        <Box style={{ minWidth: 0 }}>
          <Text size="sm" fw={600} truncate>
            {sessionLabel(session)}
          </Text>
          <Text size="xs" c="dimmed" truncate>
            {metaLine(session)}
          </Text>
        </Box>
      </Group>

      <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
        {session.is_leading && !isMissed && (
          <Badge
            size="sm"
            variant="light"
            color="medleyGreen"
            style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
          >
            Leading
          </Badge>
        )}
        <Badge
          size="sm"
          variant="light"
          color={badge.color}
          leftSection={badge.dot ? <Box w={6} h={6} style={{ borderRadius: '50%', backgroundColor: 'currentColor' }} /> : undefined}
          style={{ textTransform: 'uppercase', letterSpacing: 0.3 }}
        >
          {badge.label}
        </Badge>
        {isMissed ? null : session.is_live ? (
          session.is_leading ? (
            <Button component={Link} to={leaderActivePath} size="xs" color="medleyGreen">
              Rejoin session
            </Button>
          ) : (
            <Button component={Link} to={waitingRoomPath} size="xs" color="medleyGreen">
              Join session
            </Button>
          )
        ) : session.is_ongoing && !session.is_leading ? (
          // MED-1225 (AC3): the session is ongoing but not yet started (its start
          // time has passed, it isn't live, and it isn't missed). A late
          // participant joins via the waiting room, which polls and auto-admits
          // the moment the leader starts it — so joining works before the session
          // is live (same route + treatment as the live-now Join above).
          <Button component={Link} to={waitingRoomPath} size="xs" color="medleyGreen">
            Join session
          </Button>
        ) : session.is_ongoing && session.is_leading && session.prep_unlocked ? (
          // MED-1225 (AC4): the session is ongoing but the peer leader hasn't
          // started it (they're late, or dropped before starting). Route to the
          // prep page, which hosts the Start button (leaders are redirected
          // reflection→prep). This is the "late/dropped leader can still start
          // it from the dashboard" path.
          <Button component={Link} to={reflectionPath} size="xs" color="medleyGreen">
            Start session
          </Button>
        ) : session.is_leading && session.prep_unlocked ? (
          // MED-1141 (AC7): the leader's path back to prep persists until the
          // session starts; label flips to "Review prep" once prep is done.
          // MED-1144 (QA follow-up): also gated on prep_unlocked — while prep is
          // sequence-locked the prep page rejects access ("Session N−1 must
          // complete"), so the CTA is suppressed (the Leading badge stays). The
          // button reappears the moment the prior session completes.
          <Button
            component={Link}
            to={reflectionPath}
            size="xs"
            variant="light"
            color="medleyGreen"
          >
            {session.prep_outstanding ? 'Start Session Prep' : 'Review prep'}
          </Button>
        ) : canReachReflection ? (
          // MED-1141 (AC1/AC4): net-new participant action — "Complete" until the
          // reflection is done, then "Review" to read/update it before start.
          <Button
            component={Link}
            to={reflectionPath}
            size="xs"
            variant="light"
            color="medleyGreen"
          >
            {session.reflection_complete ? 'Review' : 'Complete'}
          </Button>
        ) : null}
      </Group>
    </Group>
  );
}

export function UpcomingSessionsSection({
  sessions,
  cohortId,
}: {
  sessions: DashboardRefreshUpcomingSession[];
  /** MED-1127 (AC11) — backs the live leader's "Rejoin session" row route. */
  cohortId: string | null;
}) {
  return (
    <Card withBorder radius="md" padding="md" mb="lg">
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Calendar size={16} />
          <Text fw={600} size="sm">
            Upcoming Sessions
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          {sessions.length} {sessions.length === 1 ? 'session' : 'sessions'}
        </Text>
      </Group>

      {sessions.length === 0 ? (
        <Text size="sm" c="dimmed" py="sm">
          No upcoming sessions scheduled.
        </Text>
      ) : (
        <Stack gap={0}>
          {sessions.map((session, i) => (
            <Box key={session.id}>
              {i > 0 && <Divider my="xs" />}
              <SessionRow session={session} cohortId={cohortId} />
            </Box>
          ))}
        </Stack>
      )}
    </Card>
  );
}
